import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  OnInit,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  filter,
  finalize,
  fromEvent,
  map,
  shareReplay,
  tap,
} from 'rxjs';
import { v4 as uuid } from 'uuid';
import { untilDestroyed } from '@ngneat/until-destroy';
import cloneDeep from 'lodash.clonedeep';

import { DprCommentComponent } from '@dpr-details/components';
import { DprDetailsGridContainerBaseComponent } from '@dpr-details/models/component';
import { DprFreeTextFieldSyncResult } from '@dpr-details/models/sync';
import { DprDetailsService } from '@dpr-details/services';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { StaticMessage } from '@shared/enums';
import { DprFreeTextField, DprFreeTextFieldDetail } from '@shared/models/dpr';
import { Sync } from '@shared/models/grid';
import { FreeTextField, FreeTextFieldItem } from '@shared/models/project-setup';
import { MergeService, ScreenSizeService } from '@shared/services';
import { SyncService } from '@shared/services/grid';

@Component({
  selector: 'app-dpr-free-text-fields-page',
  templateUrl: './dpr-free-text-fields-page.component.html',
  styleUrls: ['./dpr-free-text-fields-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprFreeTextFieldsPageComponent
  extends DprDetailsGridContainerBaseComponent<DprFreeTextField>
  implements OnInit
{
  dprFreeTextFieldItemsSubscription = new Map<number, Subscription>();
  StaticMessage = StaticMessage;
  freeTextFieldTitle: string;
  preselectedItem: string | number;

  private dprFreeTextFieldData = new Map<number, DprFreeTextFieldDetail>();
  private activatedComponent$ = new BehaviorSubject<ComponentRef<any>>(null);
  private pageRoutePath: string;

  constructor(
    private router: Router,
    protected activatedRoute: ActivatedRoute,
    private mergeService: MergeService,
    private syncService: SyncService,
    private dprDetailsService: DprDetailsService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    protected screenSizeService: ScreenSizeService
  ) {
    super(activatedRoute);
  }

  get freeTextFieldItems(): FreeTextFieldItem[] {
    return (
      this.selectedSubproject?.data?.freeTextFields?.find(
        (ftf) => ftf.id === this.freeTextFieldId
      )?.values ?? []
    );
  }

  get dprFreeTextField(): DprFreeTextField {
    // TODO(improvement): revise loading logic to be moved to a lower level.
    // until then ternary operator is needed, because the first emission of dprDetails is undefined (data is not ready yet).
    return (
      this.dprDetails?.dprFreeTextFields?.find(
        (ftf) => ftf.freeTextFieldId === this.freeTextFieldId
      ) ??
      ({
        id: this.dprFreeTextFieldId,
        dprDetailId: this.dprDetails?.id,
        freeTextFieldId: this.freeTextFieldId,
        dprFreeTextFieldDetails: [],
      } as DprFreeTextField)
    );
  }

  private get freeTextField(): FreeTextField {
    return this.selectedSubproject?.data?.freeTextFields.find(
      (ftf) => ftf.id === this.freeTextFieldId
    );
  }

  private get subTab(): number {
    const parts = this.router.url.split('/');

    return +parts[parts.length - 1];
  }

  private get freeTextFieldId(): number {
    return +this.activatedRoute?.snapshot.params.freeTextFieldId;
  }

  private get dprFreeTextFieldId(): number | string {
    return (
      this.dprDetails.dprFreeTextFields.find(
        (x) => x.freeTextFieldId === this.freeTextFieldId
      )?.id ?? uuid()
    );
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.pageRoutePath = this.activatedRoute.snapshot.routeConfig.path;

    this.selectedSubproject$
      .pipe(
        untilDestroyed(this),
        tap((selectedSubproject) => {
          const freeTextFieldItems =
            selectedSubproject?.data?.freeTextFields.find(
              (ftf) => ftf.id === this.freeTextFieldId
            )?.values ?? [];

          this.freeTextFieldTitle =
            this.selectedSubproject.data?.freeTextFields.find(
              (ftf) => ftf.id === this.freeTextFieldId
            )?.name ??
            $localize`:@@dprDetails.dprFreeTextFieldsPage.titleFreeTextField:Free Text Field`;

          const selectedTab = freeTextFieldItems.find(
            (x) => x.id === this.subTab
          );

          this.preselectedItem = selectedTab
            ? this.subTab
            : freeTextFieldItems[0].id;

          // This is necessary in order to automatically redirect to the first tab if its missing from the url
          // or to the base page if the selected tab does not exist in the list
          if (!selectedTab) {
            this.router.navigate(
              freeTextFieldItems?.length ? [freeTextFieldItems[0].id] : ['./'],
              {
                relativeTo: this.activatedRoute,
                queryParamsHandling: 'merge',
                replaceUrl: true,
              }
            );
          }
        })
      )
      .subscribe();

    // subscribe needed here to retrieve the offline changes
    this.dprDetailsOfflineChanges$.pipe(untilDestroyed(this)).subscribe();

    this.activatedComponent$
      .pipe(
        untilDestroyed(this),
        filter((activatedComponent) => !!activatedComponent),
        tap((activatedComponent) => {
          this.onActivatedComponentChanged(activatedComponent);
        })
      )
      .subscribe();

    this.dprDetails$
      .pipe(
        untilDestroyed(this),
        tap(() => {
          // this is needed to refresh the current component when data is reloaded
          this.setupRouteComponent(this.subTab);
        })
      )
      .subscribe();

    //updates comment and store changes when user navigates back/forward
    fromEvent(window, 'popstate')
      .pipe(
        untilDestroyed(this),
        tap((event) => {
          const parts = (event.target as Window).location.pathname.split('/');
          const id = +parts[parts.length - 1];
          if (
            // path last 3 part will be .../free-text-fields/:freeTextFieldId/:freeTextFieldItemId
            // pageRoutePath will be free-text-fields/:freeTextFieldId
            // comparing if parts[parts.length - 3] and this.pageRoutePath.split('/')[0] are both 'free-text-fields'
            parts[parts.length - 3] === this.pageRoutePath.split('/')[0] &&
            !!id
          ) {
            this.storeComponentChanges();
            this.setupRouteComponent(id);
          }
        })
      )
      .subscribe();
  }

  save(
    _?,
    _isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    this.storeComponentChanges();

    const saveChanges$ = this.saveChanges(isAutoSyncFromAdminChange).pipe(
      finalize(() => (this.isSaving = false)),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  onComponentActivated(component: ComponentRef<any>): void {
    this.activatedComponent$.next(component);
  }

  onSubtabChanged(subtabId: number): void {
    this.storeComponentChanges();
    this.setupRouteComponent(subtabId);
    this.preselectedItem = subtabId;
  }

  trackSubtabs(_index: number, subtab: FreeTextField): number {
    return subtab?.id;
  }

  //#region Refresh data

  protected refreshData(): void {
    this.dprFreeTextFieldData.clear();
    this.isDirty$.next(false);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  private saveChanges(
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprFreeTextFieldSyncResult> {
    const syncDPRFreeTextFieldDetails = this.buildDprFreeTextFieldDetailsSync();

    const syncData =
      this.dprDetailsSyncModelBuilderService.forDprFreeTextFields(
        this.selectedSubproject.id,
        this.dprDetails.id,
        syncDPRFreeTextFieldDetails,
        this.freeTextFieldId,
        this.dprFreeTextField.id
      );

    const saveChanges$ = this.dprDetailsService
      .syncDprFreeTextField(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => this.onSaveSuccess(result),
      error: (error: HttpErrorResponse) =>
        this.saveErrorHandlerService.onSaveError(
          this.freeTextField.name,
          this.selectedSubproject.id,
          error
        ),
    });

    return saveChanges$;
  }

  private onSaveSuccess(result): void {
    this.offlineFacade.setDprFreeTextField(result, this.dprDetails.id);

    this.setupRouteComponent(this.subTab);

    this.refreshData();
  }

  private onActivatedComponentChanged(component: ComponentRef<any>): void {
    this.storeComponentChanges();

    this.activatedComponent = component;

    this.setupRouteComponent(this.subTab);
  }

  private storeComponentChanges(): void {
    if (!this.activatedComponent) {
      return;
    }

    const currentFreeTextFieldItem: DprFreeTextFieldDetail =
      this.dprFreeTextField.dprFreeTextFieldDetails?.find(
        (detail) => detail.freeTextFieldItemId === this.subTab
      ) ?? {
        id: uuid(),
        dprFreeTextFieldId: this.dprFreeTextField.id,
        freeTextFieldItemId: this.subTab,
        comment: '',
      };

    const component = this.activatedComponent as unknown as DprCommentComponent;

    if (
      currentFreeTextFieldItem?.comment !== component.commentFormControl.value
    ) {
      this.dprFreeTextFieldData.set(this.subTab, {
        ...currentFreeTextFieldItem,
        comment: component.commentFormControl.value?.trim(),
      });
    }
  }

  private setupRouteComponent(id: number): void {
    if (!this.activatedComponent) {
      return;
    }

    const dprFreeTextFieldComment =
      (
        this.dprFreeTextFieldData.get(id) ??
        this.dprFreeTextField.dprFreeTextFieldDetails?.find(
          (x) => x.freeTextFieldItemId === id
        )
      )?.comment ?? '';

    const component = this.activatedComponent as unknown as DprCommentComponent;

    component.comment = dprFreeTextFieldComment;
    component.canEdit = this.canEdit;

    const changes: SimpleChanges = {
      comment: new SimpleChange(null, dprFreeTextFieldComment, true),
      canEdit: new SimpleChange(null, this.canEdit, true),
    };

    component.ngOnChanges(changes);

    this.dprFreeTextFieldItemsSubscription?.get(id)?.unsubscribe();
    this.dprFreeTextFieldItemsSubscription?.set(
      id,
      component.commentChanged.pipe(untilDestroyed(this)).subscribe(() => {
        this.isDirty$.next(true);
      })
    );

    this.setupCanEditBinding();
  }

  private buildDprFreeTextFieldDetailsSync(): Sync<DprFreeTextFieldDetail> {
    const changes =
      this.syncService.getDefaultSyncModel<DprFreeTextFieldDetail>();

    Array.from(this.dprFreeTextFieldData.values())
      .filter((x) => !!x)
      .forEach((detail) =>
        !isNaN(+detail.id)
          ? changes.updatedList.push(detail)
          : changes.addedList.push(detail)
      );

    const offlineChanges =
      cloneDeep(
        this.dprDetailsOfflineChanges?.dprFreeTextFields?.find(
          (x) => x.freeTextFieldId === this.freeTextFieldId
        )?.syncDprFreeTextFieldDetails
      ) ?? this.syncService.getDefaultSyncModel();

    return this.mergeService.mergeChanges(offlineChanges, changes);
  }
}
